/**
 * `buyerOrders.*` — a buyer reading their own order history (spec 39).
 *
 * Read-only surface. Everything a buyer sees here is what they placed:
 * `capturedPricePaise` is the price AS PLACED and never the current one
 * (spec 39 R3 · §5 "illegal").
 */
import { z } from "zod";
import { uuid, moneyPaise } from "../_common.js";

/** Our order machine (spec 16 R5). `draft` renders to the buyer as "Placed". */
export const buyerOrderStatus = z.enum([
  "draft",
  "confirmed",
  "approved",
  "fulfilled",
  "rejected",
]);

export const buyerOrderCard = z.object({
  orderId: uuid,
  /** `ST-xxxxxxxx` until the SO number exists at confirm, then the SO number (spec 37). */
  ref: z.string(),
  status: buyerOrderStatus,
  placedAt: z.string(),
  packCount: z.number().int(),
  pieceCount: z.number().int(),
  totalPaise: moneyPaise,
  kind: z.enum(["at_once", "prebook"]),
  deliveryStartsOn: z.string().nullable(),
  deliveryEndsOn: z.string().nullable(),
  /** First line's description — the card's one-glance "what was this". */
  firstLineLabel: z.string().nullable(),
  lineCount: z.number().int(),
});

export const listOrdersInput = z.object({
  storeSlug: z.string().min(1).max(64),
  cursor: z.string().nullish(),
  limit: z.number().int().min(1).max(50).default(20),
});

export const listOrdersOutput = z.object({
  orders: z.array(buyerOrderCard),
  nextCursor: z.string().nullable(),
});

export const getOrderInput = z.object({
  storeSlug: z.string().min(1).max(64),
  orderId: uuid,
});

export const buyerOrderLine = z.object({
  lineId: uuid,
  position: z.number().int(),
  description: z.string().nullable(),
  qty: z.number().int(),
  /** Price as placed. Never re-priced for display. */
  capturedPricePaise: moneyPaise,
  lineTotalPaise: moneyPaise,
  /**
   * Enrichment resolved from the line's pinned pack/listing refs (spec 13 R7).
   * All null on legacy lines placed before checkout captured the refs — the
   * UI falls back to `description`.
   */
  productName: z.string().nullable(),
  productSlug: z.string().nullable(),
  imageUrl: z.string().nullable(),
  packName: z.string().nullable(),
  packPieces: z.number().int().nullable(),
  /** `"S:1,M:2,L:2"` in the size scale's declared order, as placed. */
  composition: z.string().nullable(),
});

export const buyerOrderEvent = z.object({
  /** Buyer-facing label — staff-facing reasons are never included. */
  label: z.string(),
  at: z.string(),
});

export const getOrderOutput = z.object({
  orderId: uuid,
  ref: z.string(),
  status: buyerOrderStatus,
  placedAt: z.string(),
  kind: z.enum(["at_once", "prebook"]),
  deliveryStartsOn: z.string().nullable(),
  deliveryEndsOn: z.string().nullable(),
  shipToText: z.string().nullable(),
  /**
   * The seller's public phone (0149 view), for the contact card's WhatsApp
   * link. `null` when the store recorded none — the screen omits the card
   * rather than drawing a control that opens nothing.
   */
  storePhone: z.string().nullable(),
  subtotalPaise: moneyPaise,
  totalPaise: moneyPaise,
  packCount: z.number().int(),
  pieceCount: z.number().int(),
  lines: z.array(buyerOrderLine),
  timeline: z.array(buyerOrderEvent),
});
